import { useState, useCallback, useMemo, useEffect, useRef, lazy, Suspense } from 'react';
import { SearchInput } from './components/SearchInput';
import { PropertyCard } from './components/PropertyCard';
import { SkeletonLoader } from './components/SkeletonLoader';
import { EmptyState } from './components/EmptyState';
import { IOSInstallModal } from './components/IOSInstallModal';
import { searchProperties, exportToCSV } from './services/propertyService';
import { trackSearchEvent, trackEvent } from './utils/analytics';
import { fetchInflationData } from './utils/inflation';
import { generatePropertyId } from './utils/formatters';
import { Property } from './types';

// Carregado sob demanda: o DashboardSummary arrasta o recharts (~1/3 do bundle)
// e só aparece depois que uma busca retorna resultados.
const DashboardSummary = lazy(() =>
  import('./components/DashboardSummary').then((m) => ({ default: m.DashboardSummary }))
);

export default function App() {
  const [rawData, setRawData] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialSearchTerm, setInitialSearchTerm] = useState('');
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  // 1. Inicialização e Detecção de Dispositivo
  useEffect(() => {
    // Detecta iOS
    const isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);
    setIsIOS(isIPhone);

    // Detecta se já está instalado
    const standalone = (window.navigator as Navigator & { standalone?: boolean }).standalone
      || window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    try {
      const path = window.location.pathname.substring(1);
      if (path && path !== 'index.html' && !path.startsWith('api/')) {
        const lowerPath = path.toLowerCase();
        if (path !== lowerPath) {
           window.history.replaceState(null, '', `/${lowerPath}`);
        }
        const decodedTerm = decodeURIComponent(lowerPath).replace(/-/g, ' ');
        setInitialSearchTerm(decodedTerm);
      }
    } catch (e) {
      console.warn("Não foi possível ler o slug da URL:", e);
    }
    fetchInflationData();
  }, []);

  // 2. Setup PWA Install Prompt (Android/Desktop)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPrompt(e);
      console.log("PWA Prompt detectado para Android/Desktop");
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      trackEvent('ios_install_guide_opened');
      return;
    }

    if (!installPrompt) return;
    await installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;
    trackEvent('pwa_install_' + choiceResult.outcome);
    setInstallPrompt(null);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = 'RDK Imóveis - Inteligência Imobiliária';
    const text = currentSearchTerm 
      ? `Confira as transações imobiliárias para: ${currentSearchTerm}`
      : 'Consulte transações reais e histórico de propriedades.';

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        trackEvent('share_link', { method: 'native' });
      } catch {
        console.debug("Compartilhamento cancelado");
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        trackEvent('share_link', { method: 'clipboard' });
        setTimeout(() => setCopied(false), 2000);
      } catch {
        alert("Não foi possível copiar o link.");
      }
    }
  };

  const groupedData = useMemo(() => {
    if (!rawData.length) return [];
    const groups: { [key: string]: Property } = {};
    rawData.forEach((item) => {
      const key = `${item.endereco}-${item.data_transacao}`;
      if (!groups[key]) {
        groups[key] = { ...item, is_grouped: false, items: [item] };
      } else {
        const group = groups[key];
        group.is_grouped = true;
        group.items?.push(item);
        group.valor_transacao += item.valor_transacao;
        group.valor_financiado = (group.valor_financiado || 0) + (item.valor_financiado || 0);
        group.area_construida += item.area_construida;
        group.area_terreno = Math.max(group.area_terreno, item.area_terreno);
      }
    });
    return Object.values(groups);
  }, [rawData]);

  const handleSearch = useCallback(async (term: string) => {
    // Cancela qualquer busca anterior ainda em andamento para evitar que uma
    // resposta antiga chegue depois e sobrescreva o resultado mais recente.
    searchAbortControllerRef.current?.abort();

    if (!term) {
      setRawData([]);
      setHasSearched(false);
      if (window.location.pathname !== '/') window.history.replaceState(null, '', '/');
      return;
    }

    trackSearchEvent(term);
    setCurrentSearchTerm(term);
    const slug = term.trim().replace(/\s+/g, '-').toLowerCase();

    try {
      const targetPath = `/${slug}`;
      if (window.location.pathname.toLowerCase() !== targetPath && !window.location.pathname.startsWith('/api/')) {
        window.history.replaceState(null, '', targetPath);
      }
    } catch {
      // history.replaceState pode falhar em contextos restritos (ex: sandboxed iframe); ignorar.
    }

    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    setLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      // Garante que os índices de inflação estão atualizados antes da busca
      await fetchInflationData();
      const results = await searchProperties(term, controller.signal);
      if (controller.signal.aborted) return;
      setRawData(results);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Erro ao consultar dados.');
      setRawData([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const handleExport = async () => {
    trackEvent('export_data', { record_count: rawData.length });
    try {
      await exportToCSV(rawData);
    } catch (err) {
      console.error('Falha ao exportar:', err);
      setError('Não foi possível gerar o arquivo de exportação.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-20">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-brand-600 p-1.5 rounded-lg shadow-sm">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-brand-900 tracking-tight">RDK <span className="text-brand-600">IMÓVEIS</span></h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Share Button */}
            <button
              onClick={handleShare}
              className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-all border shadow-sm ${copied ? 'bg-green-50 text-green-700 border-green-200' : 'text-gray-600 hover:text-brand-600 bg-white hover:bg-slate-50 border-gray-200'}`}
              title="Compartilhar Busca"
            >
              {copied ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              )}
              <span className="hidden md:inline">{copied ? 'Copiado!' : 'Compartilhar'}</span>
            </button>

            {/* Smart Install Button (iOS detection or beforeinstallprompt) */}
            {(installPrompt || (isIOS && !isStandalone)) && (
              <button
                onClick={handleInstallClick}
                className="flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 border border-brand-100 px-3 py-2 rounded-lg transition-all shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="hidden md:inline">Instalar App</span>
              </button>
            )}

            {rawData.length > 0 && (
              <button 
                onClick={handleExport}
                className="flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-white bg-white hover:bg-brand-600 px-4 py-2 rounded-lg transition-all border border-brand-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Exportar</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className={`transition-all duration-500 ease-in-out ${groupedData.length > 0 ? 'mb-8' : 'mb-10 text-center pt-10'}`}>
          {!hasSearched && (
            <>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">Painel de Inteligência</h2>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">Consulte transações reais e histórico de propriedades com precisão.</p>
            </>
          )}
          <SearchInput onSearch={handleSearch} isLoading={loading} initialValue={initialSearchTerm} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-center animate-fade-in break-words">
            <strong className="font-bold block mb-1">Erro de Conexão:</strong>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonLoader key={i} />)}
          </div>
        ) : (
          <>
            {!loading && hasSearched && rawData.length > 0 && (
              <Suspense fallback={<div className="h-64 mb-8 rounded-xl bg-white border border-gray-100 animate-pulse" />}>
                <DashboardSummary data={rawData} />
              </Suspense>
            )}

            {groupedData.length > 0 ? (
              <div className="flex flex-col lg:flex-row gap-8 animate-fade-in-up">
                <div className="lg:w-2/3 order-2 lg:order-1">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                      {groupedData.length} transações consolidadas
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-6">
                    {groupedData.map((property) => (
                      <PropertyCard
                        key={generatePropertyId(property)}
                        property={property}
                        id={generatePropertyId(property)}
                      />
                    ))}
                  </div>
                </div>

                <div className="lg:w-1/3 order-1 lg:order-2">
                  <div className="sticky top-24 space-y-4">
                    <div className="bg-white p-1 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-100 p-3 flex items-center gap-2">
                         <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-700">Mapa de Transações</span>
                      </div>
                      <div className="h-64 lg:h-[400px] w-full relative bg-gray-100">
                        {groupedData[0] && (
                          <iframe 
                            width="100%" 
                            height="100%" 
                            className="border-0" 
                            loading="lazy" 
                            title="Mapa"
                            src={`https://maps.google.com/maps?q=${encodeURIComponent(groupedData[0].endereco + ", São Paulo, SP")}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                          >
                          </iframe>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState hasSearched={hasSearched} />
            )}
          </>
        )}
      </main>

      <IOSInstallModal 
        isOpen={showIOSModal} 
        onClose={() => setShowIOSModal(false)} 
      />
    </div>
  );
}